import { useEffect, useState, useMemo } from "react";
import { Bell, CheckCheck, X } from "lucide-react";
import { initMerchantSocket, getSocket } from "./socketClient";
import api from "./axiosConfig";

export default function MerchantNotifications({ merchantId }) {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);

  const hasNew = useMemo(
    () => notifications.some((n) => !n.read),
    [notifications]
  );

  useEffect(() => {
    if (!merchantId) return;

    const targetKey = `merchant:${String(merchantId)}`;

    // 1️⃣ Charger l’historique DB
    const fetchNotifs = async () => {
      try {
        const res = await api.get("/notifications", {
          params: { targetKey, limit: 50 },
        });
        setNotifications(res.data.notifications || []);
      } catch {}
    };

    fetchNotifs();

    // 2️⃣ Temps réel
    initMerchantSocket(merchantId);
    const socket = getSocket();

    const onNotification = (payload) => {
      setNotifications((prev) => {
        if (payload?._id && prev.some((n) => n._id === payload._id)) {
          return prev;
        }
        return [{ ...payload, read: false }, ...prev].slice(0, 50);
      });
    };

    socket.on("notification", onNotification);

    return () => {
      socket.off("notification", onNotification);
    };
  }, [merchantId]);

  // ✅ Tout lire (DB + UI)
  const markAllRead = async () => {
    const targetKey = `merchant:${String(merchantId)}`;

    try {
      await api.put("/notifications/read-all", { targetKey });
    } catch {}

    setNotifications((prev) =>
      prev.map((n) => ({ ...n, read: true }))
    );
  };

  // 🧼 Effacer (DB + UI) ✅
  const clearAll = async () => {
    const targetKey = `merchant:${String(merchantId)}`;

    try {
      await api.delete("/notifications", {
        data: { targetKey },
      });
    } catch {}

    setNotifications([]);
  };

  return (
    <div className="relative">
      {/* 🔔 Bell */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`relative p-2.5 rounded-xl transition-all ${
          open
            ? "bg-white text-[#f34141] shadow-lg scale-110"
            : "text-white hover:bg-white/20"
        }`}
      >
        <Bell size={24} />
        {hasNew && (
          <span className="absolute top-1.5 right-1.5 flex h-3.5 w-3.5">
            <span className="absolute inline-flex w-full h-full bg-yellow-400 rounded-full opacity-75 animate-ping"></span>
            <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-yellow-500 border-2 border-[#f34141]"></span>
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />

          <div className="absolute right-0 mt-4 w-80 bg-white rounded-[2rem] shadow-2xl border z-50">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <span className="font-black text-slate-800">Notifications</span>
              <button onClick={() => setOpen(false)}>
                <X size={18} className="text-slate-400" />
              </button>
            </div>

            {/* Actions */}
            {notifications.length > 0 && (
              <div className="flex gap-2 px-4 py-2 border-b bg-gray-50">
                <button
                  onClick={markAllRead}
                  className="px-3 py-1 text-xs font-black bg-white border rounded-lg hover:bg-gray-100"
                >
                  <CheckCheck size={14} /> Tout lire
                </button>
                <button
                  onClick={clearAll}
                  className="px-3 py-1 text-xs font-black bg-white border rounded-lg hover:bg-red-500 hover:text-white"
                >
                  Effacer
                </button>
              </div>
            )}

            {/* List */}
            <div className="max-h-[400px] overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="py-12 font-bold text-center text-slate-400">
                  Aucune notification
                </div>
              ) : (
                notifications.map((n) => (
                  <div
                    key={n._id}
                    className={`px-6 py-4 border-b ${
                      !n.read
                        ? "bg-red-50/40 border-l-4 border-[#f34141]"
                        : "hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex justify-between mb-1">
                      <p className="text-sm font-black text-slate-800">
                        {n.title}
                      </p>
                      <span className="text-[10px] text-slate-400">
                        {new Date(n.createdAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500">{n.body}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}